import { ZfLoadingSpinner, ZfStack, ZfTextButton, ZfTypography } from '@zafire/react';
import type { FunctionComponent } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCreateAdvisoryNeed, useDocuments, useEnabledDomiciles, useTools, useUpdateAdvisoryNeed } from '../../../api';
import type { AdvisoryNeedDto, DocumentDto, DomicileDto } from '../../../api/generated/client';
import type { ITool } from '../../../interfaces/tool';
import { MultiLanguageEditor } from '../../atoms';
import { DomicileSelection } from '../domicile-selection';
import { ListEditorDnd } from '../drag-and-drop';

interface IProps {
    need?: AdvisoryNeedDto;
}

const getInitialAdvisoryNeedProperty = <TPropItem extends { id: number }>(propIds: number[] = [], allPropItems: TPropItem[] = []): TPropItem[] =>
    propIds.map((id) => allPropItems.find((item) => item.id === id)).filter((item) => item != null);

export const AdvisoryNeedEditor: FunctionComponent<IProps> = ({ need }) => {
    const { t } = useTranslation();
    const { data: availableDomiciles } = useEnabledDomiciles();
    const { data: allDocs } = useDocuments();
    const { data: allTools } = useTools();

    const { mutate: createAdvisoryNeed } = useCreateAdvisoryNeed();
    const { mutate: updateAdvisoryNeed } = useUpdateAdvisoryNeed();

    const checkListsInitial = useMemo(() => getInitialAdvisoryNeedProperty(need?.checkListIds, allDocs), [need?.checkListIds, allDocs]);
    const knowHowsInitial = useMemo(() => getInitialAdvisoryNeedProperty(need?.knowHowIds, allDocs), [need?.knowHowIds, allDocs]);
    const toolsInitial = useMemo(() => getInitialAdvisoryNeedProperty(need?.toolIds, allTools), [need?.toolIds, allTools]);

    const hasChanges = useRef<boolean>(false);
    const [advisoryNeed, setAdvisoryNeed] = useState(need);
    const [checklists, setChecklists] = useState(checkListsInitial);
    const [knowHows, setKnowHows] = useState(knowHowsInitial);
    const [tools, setTools] = useState(toolsInitial);

    useEffect(() => setChecklists(checkListsInitial), [checkListsInitial]);
    useEffect(() => setKnowHows(knowHowsInitial), [knowHowsInitial]);
    useEffect(() => setTools(toolsInitial), [toolsInitial]);
    useEffect(() => setAdvisoryNeed(need), [need]);

    const handleDomicileChange = (includedDomiciles: DomicileDto[], excludedDomiciles: DomicileDto[], global: boolean) => {
        hasChanges.current = true;
        setAdvisoryNeed((prevState) => ({
            ...prevState!,
            global,
            excludedDomiciles,
            includedDomiciles,
        }));
    };

    const handleAdvisoryNeedChange = (property: keyof AdvisoryNeedDto) => (value: AdvisoryNeedDto[keyof AdvisoryNeedDto]) =>
        setAdvisoryNeed((prevState) => {
            hasChanges.current = true;

            return { ...prevState!, [property]: value };
        });

    const handleCheckListsChange = (_checklists: DocumentDto[]) => {
        hasChanges.current = true;
        setChecklists([..._checklists]);
    };

    const handleKnowHowChange = (_knowHows: DocumentDto[]) => {
        hasChanges.current = true;
        setKnowHows([..._knowHows]);
    };

    const handleToolsChange = (_tools: ITool[]) => {
        hasChanges.current = true;
        setTools([..._tools]);
    };

    const handleSaveChanges = () => {
        if (!advisoryNeed) return;

        const payload: AdvisoryNeedDto = {
            ...advisoryNeed,
            checkListIds: checklists.map((doc) => doc.id),
            knowHowIds: knowHows.map((doc) => doc.id),
            toolIds: tools.map((tool) => tool.id),
        };

        if (advisoryNeed.id === -1) createAdvisoryNeed(payload);
        else updateAdvisoryNeed(payload);

        hasChanges.current = false;
    };

    if (!advisoryNeed) return <ZfLoadingSpinner />;

    return (
        <ZfStack direction="column" spacing="large">
            <ZfStack direction="row" alignment="center" justify="space-between">
                <ZfTypography variant="heading-small" style={{ flex: 1 }}>
                    {advisoryNeed.id > -1 ? `${t('zfEventModalEditNeed')}: ID ${advisoryNeed.id}` : t('contentAdmin.createNewAdvisoryNeed')}
                </ZfTypography>
                <ZfTextButton
                    hierarchy="secondary"
                    onClick={handleSaveChanges}
                    style={{ alignSelf: 'end' }}
                    disabled={!advisoryNeed.name || !advisoryNeed.description || !hasChanges.current}
                >
                    {t('contentAdmin.saveChanges')}
                </ZfTextButton>
            </ZfStack>
            <MultiLanguageEditor label={t('roadmapColHeaderTitle')} value={advisoryNeed.name} direction="row" onChange={handleAdvisoryNeedChange('name')} />
            <DomicileSelection
                global={advisoryNeed.global}
                onChange={handleDomicileChange}
                available={availableDomiciles ?? []}
                excludedIn={advisoryNeed.excludedDomiciles}
                includedIn={advisoryNeed.includedDomiciles}
            />
            <ListEditorDnd title={t('contentAdmin.checklistTitle')} selectedItems={checklists} allItems={allDocs ?? []} onChange={handleCheckListsChange} />
            <ListEditorDnd title={t('contentAdmin.knowHowsTitle')} selectedItems={knowHows} allItems={allDocs ?? []} onChange={handleKnowHowChange} />
            <ListEditorDnd title={t('contentAdmin.toolsTitle')} selectedItems={tools} allItems={allTools ?? []} onChange={handleToolsChange} />
            <MultiLanguageEditor
                label={t('titleTpDescription')}
                value={advisoryNeed.description}
                onChange={handleAdvisoryNeedChange('description')}
                multiline={true}
                markdown={true}
                collapsible={true}
                expanded={true}
            />
            <MultiLanguageEditor
                label={t('contentAdmin.proposedActionsTitle')}
                value={advisoryNeed.proposedActions}
                onChange={handleAdvisoryNeedChange('proposedActions')}
                multiline={true}
                markdown={true}
                collapsible={true}
                expanded={true}
            />
        </ZfStack>
    );
};
